// components/nav-bar/index.tsx
import React, { useState, useEffect } from "react";
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import Drawer from "@mui/material/Drawer";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import Divider from "@mui/material/Divider";
import { useTheme, useMediaQuery } from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import { Link, useLocation } from "react-router-dom";

const NAV_LINKS = [
  { label: "Features", href: "/#features" },
  { label: "Pricing", href: "/#pricing" },
  { label: "About", href: "/AboutUs" },
  { label: "FAQ", href: "/#faq" },
];

export default function Navbar() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  // Scroll-aware background transition
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 24);
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const isActive = (path: string) => {
    if (path.startsWith("/#")) return false; // anchor links never "active"
    return location.pathname === path;
  };

  // Mobile drawer content
  const drawer = (
    <Box sx={{ p: 2 }}>
      {/* Drawer header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 2,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Box
            component="img"
            src="/icon192.png"
            alt="Avro"
            sx={{ width: 36, height: 36, borderRadius: 2 }}
          />
          <Typography
            sx={{
              fontWeight: 800,
              fontSize: 22,
              background: "linear-gradient(180deg, #2aa880, #127351)",
              WebkitBackgroundClip: "text",
              backgroundClip: "text",
              color: "transparent",
            }}
          >
            Avro
          </Typography>
        </Box>
        <IconButton onClick={handleDrawerToggle} size="small">
          <CloseIcon />
        </IconButton>
      </Box>

      <Divider sx={{ mb: 1 }} />

      <List>
        {NAV_LINKS.map((link) => (
          <ListItem key={link.label} disablePadding>
            <ListItemButton
              component={link.href.startsWith("/#") ? "a" : Link}
              {...(link.href.startsWith("/#")
                ? { href: link.href }
                : { to: link.href })}
              onClick={handleDrawerToggle}
              sx={{
                borderRadius: 2,
                mb: 0.5,
                fontWeight: isActive(link.href) ? 700 : 500,
                color: isActive(link.href) ? "#127351" : "#334155",
                "&:hover": {
                  backgroundColor: "rgba(42, 168, 128, 0.06)",
                },
              }}
            >
              <ListItemText
                primary={link.label}
                primaryTypographyProps={{ fontWeight: "inherit" }}
              />
            </ListItemButton>
          </ListItem>
        ))}
      </List>

      <Divider sx={{ my: 1 }} />

      {/* CTA in drawer */}
      <Button
        component={Link}
        to="/partners"
        variant="contained"
        fullWidth
        onClick={handleDrawerToggle}
        sx={{
          mt: 1,
          py: 1.2,
          background: "linear-gradient(180deg, #2aa880, #127351)",
          fontWeight: 700,
          borderRadius: 999,
          fontSize: 15,
          boxShadow: "0 4px 14px rgba(42, 168, 128, 0.25)",
          "&:hover": {
            background: "linear-gradient(180deg, #32b88e, #158a5f)",
            boxShadow: "0 6px 20px rgba(42, 168, 128, 0.35)",
          },
        }}
      >
        Log In
      </Button>
    </Box>
  );

  return (
    <>
      <AppBar
        sx={{
          backgroundColor: scrolled
            ? "rgba(0, 0, 0, 0.55)"
            : "rgba(0, 0, 0, 0.15)",
          backdropFilter: scrolled ? "blur(20px)" : "blur(8px)",
          WebkitBackdropFilter: scrolled ? "blur(20px)" : "blur(8px)",
          borderBottom: scrolled
            ? "1px solid rgba(255, 255, 255, 0.08)"
            : "1px solid rgba(255, 255, 255, 0.12)",
          borderRadius: scrolled ? 0 : "0 0 24px 24px",
          maxWidth: "100%",
          boxShadow: scrolled
            ? "0 8px 32px rgba(0, 0, 0, 0.2)"
            : "0 4px 30px rgba(0, 0, 0, 0.06)",
          transition:
            "background-color 0.4s ease, backdrop-filter 0.4s ease, border-radius 0.4s ease, box-shadow 0.4s ease, border-color 0.4s ease",
        }}
        color="default"
        elevation={0}
      >
        <Toolbar
          sx={{
            maxWidth: 1280,
            alignSelf: "center",
            justifyContent: "space-between",
            width: "100%",
            py: 0.5,
          }}
        >
          {isMobile ? (
            // Mobile layout
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                width: "100%",
              }}
            >
              <IconButton component={Link} to="/" sx={{ p: 0.5 }}>
                <Box
                  component="img"
                  src="/icon192.png"
                  alt="Avro"
                  sx={{
                    width: 38,
                    height: 38,
                    borderRadius: 2,
                  }}
                />
              </IconButton>
              <IconButton
                color="inherit"
                aria-label="menu"
                onClick={handleDrawerToggle}
                sx={{ color: "#fff" }}
              >
                <MenuIcon />
              </IconButton>
            </Box>
          ) : (
            // Desktop layout
            <>
              {/* Left: Logo and Name */}
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <IconButton
                  component={Link}
                  to="/"
                  sx={{ p: 0.5 }}
                >
                  <Box
                    component="img"
                    src="/icon192.png"
                    alt="Avro"
                    sx={{
                      width: 34,
                      height: 34,
                      borderRadius: 2,
                    }}
                  />
                </IconButton>
                <Typography
                  component={Link}
                  to="/"
                  sx={{
                    textDecoration: "none",
                    fontWeight: 800,
                    fontSize: 24,
                    color: "#fff",
                    letterSpacing: "-0.02em",
                    "&:hover": { opacity: 0.9 },
                    transition: "opacity 0.2s",
                  }}
                >
                  Avro
                </Typography>
              </Box>

              {/* Center: Nav links */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                }}
              >
                {NAV_LINKS.map((link) => (
                  <Button
                    key={link.label}
                    component={link.href.startsWith("/#") ? "a" : Link}
                    {...(link.href.startsWith("/#")
                      ? { href: link.href }
                      : { to: link.href })}
                    sx={{
                      color: "rgba(255,255,255,0.85)",
                      fontWeight: isActive(link.href) ? 700 : 500,
                      fontSize: 14,
                      px: 2,
                      py: 1,
                      borderRadius: 2,
                      textTransform: "none",
                      letterSpacing: "0.01em",
                      position: "relative",
                      "&:hover": {
                        color: "#fff",
                        backgroundColor: "rgba(255,255,255,0.08)",
                      },
                      "&::after": isActive(link.href)
                        ? {
                          content: '""',
                          position: "absolute",
                          bottom: 4,
                          left: "30%",
                          right: "30%",
                          height: 2,
                          borderRadius: 999,
                          background:
                            "linear-gradient(90deg, #2aa880, #127351)",
                        }
                        : {},
                    }}
                  >
                    {link.label}
                  </Button>
                ))}
              </Box>

              {/* Right: CTA */}
              <Button
                component={Link}
                to="/partners"
                variant="contained"
                sx={{
                  background: "linear-gradient(180deg, #2aa880, #127351)",
                  color: "#fff",
                  fontWeight: 700,
                  borderRadius: 999,
                  px: 3,
                  py: 0.8,
                  fontSize: 14,
                  boxShadow: "0 4px 14px rgba(42, 168, 128, 0.25)",
                  "&:hover": {
                    background: "linear-gradient(180deg, #32b88e, #158a5f)",
                    boxShadow: "0 6px 20px rgba(42, 168, 128, 0.35)",
                    transform: "translateY(-1px)",
                  },
                  transition: "all 0.25s ease",
                }}
              >
                Log In
              </Button>
            </>
          )}
        </Toolbar>
      </AppBar>

      {/* Mobile Drawer */}
      <Drawer
        anchor="top"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: "block", md: "none" },
          "& .MuiDrawer-paper": {
            boxSizing: "border-box",
            width: "100%",
            borderRadius: "0 0 24px 24px",
            boxShadow: "0 20px 60px rgba(0,0,0,0.15)",
          },
        }}
      >
        {drawer}
      </Drawer>
    </>
  );
}
